/**
 * Go to… page catalog — derived from hub modules + NAV_BY_APP (and extras).
 * Do not maintain a parallel module list in QuickSwitcher.
 */
import {
  INTEGRATOR_MODULE_DEFINITIONS,
  INTEGRATOR_SUBAPP_KEYS,
  integratorModuleLabel,
  type IntegratorSubappKey,
} from "@hub-modules";
import { navItemsForApp, type NavItem } from "./appNavigation";
import { coHomeAppKeysForPathname } from "./coHomedLayoutApp";
import { normalizeQuickNavPath } from "./quickSwitcherPageRank";

export type QuickSwitcherCatalogPage = {
  path: string;
  label: string;
  group: string;
  searchText?: string;
  owningAppKeys: readonly string[];
};

/** Pages that exist as routes but are not module-tab links. */
export const EXTRA_QUICK_NAV: readonly { path: string; label: string; appKey: IntegratorSubappKey }[] = [
  {
    path: "/mrp/purchase-orders/new",
    label: "New purchase order",
    appKey: "mrp",
  },
  {
    path: "/mrp/suppliers/new",
    label: "New supplier",
    appKey: "mrp",
  },
  {
    path: "/mrp/tla-planning/detail",
    label: "TLA planning detail",
    appKey: "mrp",
  },
  {
    path: "/bom/test-revision",
    label: "BoM test revision",
    appKey: "bom",
  },
  {
    path: "/manufacturing/kitting/pick",
    label: "Kitting pick",
    appKey: "manufacturing",
  },
  {
    path: "/manufacturing/configuration/alternate",
    label: "Configuration alternate",
    appKey: "manufacturing",
  },
  {
    path: "/manufacturing/final-qc/mistaken-pass",
    label: "Final QC mistaken pass",
    appKey: "manufacturing",
  },
  {
    path: "/manufacturing/pre-encapsulation/settings",
    label: "Pre-encapsulation settings",
    appKey: "manufacturing",
  },
  {
    path: "/manufacturing-management/edit-batch",
    label: "Edit batch",
    appKey: "manufacturing_management",
  },
  {
    path: "/manufacturing-management/production-capacity/settings",
    label: "Production capacity settings",
    appKey: "manufacturing_management",
  },
  {
    path: "/accounting/ap/new",
    label: "New vendor invoice",
    appKey: "accounting",
  },
  {
    path: "/quality/rmas/new",
    label: "New RMA",
    appKey: "quality",
  },
  {
    path: "/quality/settings/failure-modes",
    label: "Failure modes",
    appKey: "quality",
  },
  {
    path: "/quality/settings/operator-scripts",
    label: "Operator scripts",
    appKey: "quality",
  },
  {
    path: "/quality/settings/operator-scripts/new",
    label: "New operator script",
    appKey: "quality",
  },
  {
    path: "/quality/settings/tier-1-scripts",
    label: "Tier 1 scripts",
    appKey: "quality",
  },
  {
    path: "/quality/settings/tier-1-scripts/new",
    label: "New tier 1 script",
    appKey: "quality",
  },
  {
    path: "/inside-sales/sales-orders/new",
    label: "New sales order",
    appKey: "inside_sales",
  },
  {
    path: "/engineering/firmware/new",
    label: "New firmware version",
    appKey: "engineering",
  },
  {
    path: "/engineering/firmware/notes/new",
    label: "New firmware release note",
    appKey: "engineering",
  },
  {
    path: "/shipping/receive/rma",
    label: "Inbound RMA",
    appKey: "shipping",
  },
  {
    path: "/shipping/receive/non-po-purchase",
    label: "Non-PO purchase",
    appKey: "shipping",
  },
  {
    path: "/shipping/dev/camera-capture-layout",
    label: "Camera capture layout (dev)",
    appKey: "shipping",
  },
  {
    path: "/dev/receive-mpn-keyboard-probe",
    label: "Receive MPN keyboard probe (dev)",
    appKey: "shipping",
  },
  {
    path: "/dev/module-navigation-mockup/pc",
    label: "Module navigation mockup (PC)",
    appKey: "personal",
  },
  {
    path: "/dev/module-navigation-mockup/tablet",
    label: "Module navigation mockup (tablet)",
    appKey: "personal",
  },
  {
    path: "/a",
    label: "Arrival package (scan)",
    appKey: "shipping",
  },
  {
    path: "/i",
    label: "Carton adjust (scan)",
    appKey: "warehouse",
  },
  {
    path: "/inv",
    label: "Recount from location label",
    appKey: "warehouse",
  },
  {
    path: "/personal/safety-reports/new",
    label: "New safety report",
    appKey: "personal",
  },
];

/** Legacy URL prefixes that are not module `path` values. */
const PATH_PREFIX_ALIASES: readonly { prefix: string; key: IntegratorSubappKey }[] = [
  { prefix: "/sales-orders", key: "inside_sales" },
  { prefix: "/warehouse/carton-adjust", key: "warehouse" },
  { prefix: "/warehouse/location-adjust", key: "warehouse" },
  { prefix: "/i/", key: "warehouse" },
  { prefix: "/inv/", key: "warehouse" },
  { prefix: "/a/", key: "shipping" },
];

const modulesByPathLength = [...INTEGRATOR_MODULE_DEFINITIONS].sort(
  (left, right) => right.path.length - left.path.length,
);

export const QUICK_SWITCHER_MODULE_ROOT_PATHS = new Set(
  INTEGRATOR_MODULE_DEFINITIONS.map((row) => normalizeQuickNavPath(row.path)),
);

export function moduleKeyForQuickNavPath(pathValue: string): IntegratorSubappKey | "" {
  const normalized = normalizeQuickNavPath(pathValue.split("?")[0] ?? pathValue);
  for (const alias of PATH_PREFIX_ALIASES) {
    if (normalized === alias.prefix || normalized.startsWith(`${alias.prefix}/`)) {
      return alias.key;
    }
  }
  for (const row of modulesByPathLength) {
    const root = normalizeQuickNavPath(row.path);
    if (normalized === root || normalized.startsWith(`${root}/`)) {
      return row.key;
    }
  }
  return "";
}

function owningAppKeysForPath(pathValue: string, extras: NavItem["coHomeAppKeys"]): Set<string> {
  const normalized = normalizeQuickNavPath(pathValue.split("?")[0] ?? pathValue);
  const keys = new Set<string>(coHomeAppKeysForPathname(normalized));
  const prefixKey = moduleKeyForQuickNavPath(normalized);
  if (prefixKey !== "") {
    keys.add(prefixKey);
  }
  for (const extra of EXTRA_QUICK_NAV) {
    if (normalizeQuickNavPath(extra.path) === normalized) {
      keys.add(extra.appKey);
    }
  }
  for (const extraKey of extras ?? []) {
    keys.add(extraKey);
  }
  return keys;
}

export function appKeysOwningQuickNavPath(pathValue: string): readonly string[] {
  return [...owningAppKeysForPath(pathValue, undefined)].sort((left, right) => left.localeCompare(right));
}

export function quickNavPageVisibleToKeys(pathValue: string, visibleKeys: ReadonlySet<string>): boolean {
  return appKeysOwningQuickNavPath(pathValue).some((key) => visibleKeys.has(key));
}

function catalogPageFromNavItem(item: NavItem, appKey: string): QuickSwitcherCatalogPage {
  const owning = owningAppKeysForPath(item.to, item.coHomeAppKeys);
  owning.add(appKey);
  return {
    path: item.to,
    label: item.label,
    group: integratorModuleLabel(appKey),
    searchText: item.searchText,
    owningAppKeys: [...owning].sort((left, right) => left.localeCompare(right)),
  };
}

export function buildQuickSwitcherCatalogPages(): QuickSwitcherCatalogPage[] {
  const out: QuickSwitcherCatalogPage[] = [];
  const seen = new Set<string>();

  for (const row of INTEGRATOR_MODULE_DEFINITIONS) {
    const pathValue = row.path;
    if (seen.has(pathValue)) continue;
    seen.add(pathValue);
    out.push({
      path: pathValue,
      label: `${row.label} home`,
      group: row.label,
      searchText: `${row.key} ${row.label}`,
      owningAppKeys: appKeysOwningQuickNavPath(pathValue),
    });
  }

  for (const app of INTEGRATOR_SUBAPP_KEYS) {
    for (const item of navItemsForApp(app)) {
      if (seen.has(item.to)) continue;
      seen.add(item.to);
      out.push(catalogPageFromNavItem(item, app));
    }
  }

  for (const extra of EXTRA_QUICK_NAV) {
    if (seen.has(extra.path)) continue;
    seen.add(extra.path);
    out.push({
      path: extra.path,
      label: extra.label,
      group: integratorModuleLabel(extra.appKey),
      owningAppKeys: appKeysOwningQuickNavPath(extra.path),
    });
  }

  return out;
}
